import React from 'react';
import { Patient } from '../../types';
import { User, Calendar, Phone, Mail, MapPin, Shield, AlertTriangle } from 'lucide-react';
import { useGetConditions } from '../../services/query/user/user';

export default function OverviewTab({
  patient,
  isEditing,
  onChange
}: {
  patient: Patient;
  isEditing: boolean;
  onChange: (patient: Patient) => void;
}) {
  const { data: conditions = [] } = useGetConditions({ limit: 100 });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>

        <InfoField
          icon={<User className="w-5 h-5" />}
          type={'string'}
          label="Full Name"
          value={patient.name}
          isEditing={isEditing}
          onChange={(value) => onChange({ ...patient, name: value })}
        />

        <InfoField
          type={'date'}
          icon={<Calendar className="w-5 h-5" />}
          label="Date of Birth"
          value={patient.dateOfBirth}
          isEditing={isEditing}
          onChange={(value) => onChange({ ...patient, dateOfBirth: value })}
        />

        <InfoField
          type={''}
          icon={<Phone className="w-5 h-5" />}
          label="Phone"
          value={patient.phone}
          isEditing={isEditing}
          onChange={(value) => onChange({ ...patient, phone: value })}
        />

        <InfoField
          type={'email'}
          icon={<Mail className="w-5 h-5" />}
          label="Email"
          value={patient.email}
          isEditing={isEditing}
          onChange={(value) => onChange({ ...patient, email: value })}
        />

        <InfoField
          type={'text'}
          icon={<MapPin className="w-5 h-5" />}
          label="Address"
          value={patient.address}
          isEditing={isEditing}
          onChange={(value) => onChange({ ...patient, address: value })}
          multiline
        />

        {/* Emergency Contact */}
        <div className="bg-gray-50 rounded-lg p-4 mt-6">
          <h4 className="font-semibold text-gray-900 mb-3">Emergency Contact</h4>
          <div className="space-y-2">
            <InfoField
              type={'text'}
              icon={<User className="w-4 h-4" />}
              label="Name"
              value={patient.emergencyContact.name}
              isEditing={isEditing}
              onChange={(value) => onChange({
                ...patient,
                emergencyContact: { ...patient.emergencyContact, name: value }
              })}
              compact
            />
            <InfoField
              type={'phone'}
              icon={<Phone className="w-4 h-4" />}
              label="Phone"
              value={patient.emergencyContact.phone}
              isEditing={isEditing}
              onChange={(value) => onChange({
                ...patient,
                emergencyContact: { ...patient.emergencyContact, phone: value }
              })}
              compact
            />
            <InfoField
                type={'text'}
              icon={<User className="w-4 h-4" />}
              label="Relationship"
              value={patient.emergencyContact.relationship}
              isEditing={isEditing}
              onChange={(value) => onChange({
                ...patient,
                emergencyContact: { ...patient.emergencyContact, relationship: value }
              })}
              compact
            />
          </div>
        </div>
      </div>

      {/* Medical & Insurance Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Medical & Insurance</h3>

        {/* Insurance */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Insurance Information
          </h4>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-blue-700 font-medium">Provider</label>
              {isEditing ? (
                <input
                  type="text"
                  value={patient.insurance.provider}
                  onChange={(e) => onChange({
                    ...patient,
                    insurance: { ...patient.insurance, provider: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-blue-300 rounded mt-1"
                />
              ) : (
                <p className="text-blue-900 font-medium">{patient.insurance.provider || "--"}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-blue-700 font-medium">Policy Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={patient.insurance.policyNumber}
                    onChange={(e) => onChange({
                      ...patient,
                      insurance: { ...patient.insurance, policyNumber: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-blue-300 rounded mt-1"
                  />
                ) : (
                  <p className="text-blue-900">{patient.insurance.policyNumber || "--"}</p>
                )}
              </div>
              <div>
                <label className="text-xs text-blue-700 font-medium">Group Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={patient.insurance.groupNumber}
                    onChange={(e) => onChange({
                      ...patient,
                      insurance: { ...patient.insurance, groupNumber: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-blue-300 rounded mt-1"
                  />
                ) : (
                  <p className="text-blue-900">{patient.insurance.groupNumber || "--"}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Medical History */}
        <div className="bg-red-50 rounded-lg p-4">
          <h4 className="font-semibold text-red-900 mb-3">Medical History</h4>
          {patient.medicalHistory.length > 0 ? (
            <ul className="space-y-1">
              {patient.medicalHistory.map((item, index) => (
                <li key={index} className="text-sm text-red-900 flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-red-700">No medical history recorded</p>
          )}

          <div className="mt-4">
            <label className="block text-xs font-semibold text-red-900 mb-1">Medical Condition</label>
            {isEditing ? (
              <select
                value={patient.medical_condition ?? ''}
                onChange={(e) => onChange({ ...patient, medical_condition: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded text-sm bg-white"
              >
                <option value="">— Select condition —</option>
                {conditions.map((c) => (
                  <option key={c.condition} value={c.condition}>
                    {c.condition}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-red-900">
                {patient.medical_condition?.trim() ? patient.medical_condition : '—'}
              </p>
            )}
          </div>
        </div>

        {/* Allergies */}
        <div className="bg-orange-50 rounded-lg p-4">
          <h4 className="font-semibold text-orange-900 mb-3 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            Allergies
          </h4>
          {patient.allergies.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {patient.allergies.map((allergy, index) => (
                <span key={index} className="px-3 py-1 bg-orange-200 text-orange-900 rounded-full text-sm font-medium">
                  {allergy}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-orange-700">No known allergies</p>
          )}
        </div>

        {/* Notes */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Clinical Notes</h4>
          {isEditing ? (
            <textarea
              value={patient.notes}
              onChange={(e) => onChange({ ...patient, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded"
              rows={4}
            />
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {patient.notes || 'No notes recorded'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoField({
  icon,
  label,
  type,
  value,
  isEditing,
  onChange,
  multiline = false,
  compact = false
}: {
  icon: React.ReactNode;
  label: string;
  type: string | 'text' | 'date' | 'phone' | 'email';
  value: string;
  isEditing: boolean;
  onChange: (value: string) => void;
  multiline?: boolean;
  compact?: boolean;
}) {
  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      <label className={`flex items-center space-x-2 ${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>
        {icon}
        <span>{label}</span>
      </label>
      {isEditing ? (
        multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            rows={3}
          />
        ) : (
          type && type.length > 0 ? (
            <input
              type={type}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          ) : (
            <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-900`}>{value || "--"}</p>
          )
        )
      ) : (
        <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-900`}>{value || "--"}</p>
      )}
    </div>
  );
}
